"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_TOUCH_CONTROLS_LAYOUT,
  getTouchControlsLayout,
  getTouchControlsOrientation,
  updateTouchControlsLayout,
  type TouchControlName,
  type TouchControlsOrientation,
  type TouchControlsSettings,
} from "../hooks/useTouchControlsSettings";

type TouchLayoutEditorProps = {
  settings: TouchControlsSettings;
  onChange: (settings: TouchControlsSettings) => void;
};

const CONTROLS: ReadonlyArray<{
  name: TouchControlName;
  glyph: string;
  label: string;
}> = [
  { name: "left", glyph: "←", label: "左1マス" },
  { name: "softDropLeft", glyph: "↙", label: "左下" },
  { name: "softDrop", glyph: "▼", label: "下" },
  { name: "softDropRight", glyph: "↘", label: "右下" },
  { name: "right", glyph: "→", label: "右1マス" },
  { name: "ccw", glyph: "↺", label: "左回転" },
  { name: "cw", glyph: "↻", label: "右回転" },
];

function clamp(value: number): number {
  return Math.min(96, Math.max(4, value));
}

function orientationLabel(orientation: TouchControlsOrientation): string {
  return orientation === "portrait" ? "縦画面" : "横画面";
}

export function TouchLayoutEditor({ settings, onChange }: TouchLayoutEditorProps) {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<TouchControlName | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [editorOrientation, setEditorOrientation] = useState<TouchControlsOrientation>(getTouchControlsOrientation);
  const [draftSettings, setDraftSettings] = useState<TouchControlsSettings>(settings);
  const previewOrientation = getTouchControlsOrientation();

  const moveControl = useCallback((name: TouchControlName, clientX: number, clientY: number) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;

    const nextPosition = {
      x: clamp(((clientX - rect.left) / rect.width) * 100),
      y: clamp(((clientY - rect.top) / rect.height) * 100),
    };
    const layout = getTouchControlsLayout(settings, previewOrientation);
    onChange(updateTouchControlsLayout(settings, previewOrientation, {
      ...layout,
      [name]: nextPosition,
    }));
  }, [onChange, previewOrientation, settings]);

  const openEditor = () => {
    setDraftSettings(settings);
    setEditorOrientation(getTouchControlsOrientation());
    setDragging(null);
    setIsOpen(true);
  };

  const closeEditor = useCallback(() => {
    setDragging(null);
    setIsOpen(false);
  }, []);

  const moveDraftControl = useCallback((name: TouchControlName, clientX: number, clientY: number, stage: HTMLDivElement) => {
    const rect = stage.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const nextPosition = {
      x: clamp(((clientX - rect.left) / rect.width) * 100),
      y: clamp(((clientY - rect.top) / rect.height) * 100),
    };
    setDraftSettings((current) => {
      const layout = getTouchControlsLayout(current, editorOrientation);
      return updateTouchControlsLayout(current, editorOrientation, {
        ...layout,
        [name]: nextPosition,
      });
    });
  }, [editorOrientation]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeEditor();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeEditor, isOpen]);

  const inlineLayout = getTouchControlsLayout(settings, previewOrientation);
  const draftLayout = getTouchControlsLayout(draftSettings, editorOrientation);

  return (
    <>
      <div
        aria-label={`${orientationLabel(previewOrientation)}の画面キーパッド配置プレビュー`}
        className="touch-layout-editor"
        ref={previewRef}
        onPointerMove={(event) => {
          if (dragging) moveControl(dragging, event.clientX, event.clientY);
        }}
        onPointerUp={() => setDragging(null)}
        onPointerCancel={() => setDragging(null)}
      >
        <span className="touch-layout-board-hint">ゲーム画面 / {orientationLabel(previewOrientation)}</span>
        {CONTROLS.map(({ name, glyph, label }) => {
          const position = inlineLayout[name];
          return (
            <button
              aria-label={`${label}ボタンの位置を変更`}
              className={`touch-layout-item touch-layout-${name}${dragging === name ? " is-dragging" : ""}`}
              key={name}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.setPointerCapture(event.pointerId);
                setDragging(name);
                moveControl(name, event.clientX, event.clientY);
              }}
              onPointerMove={(event) => {
                if (dragging === name) moveControl(name, event.clientX, event.clientY);
              }}
              onPointerUp={() => setDragging(null)}
              onPointerCancel={() => setDragging(null)}
              style={{ left: `${position.x}%`, top: `${position.y}%` }}
              type="button"
            >
              <span aria-hidden="true">{glyph}</span>
              <small>{label}</small>
            </button>
          );
        })}
      </div>
      <button className="secondary-button touch-layout-open" onClick={openEditor} type="button">
        全画面で配置（縦・横）
      </button>

      {isOpen ? (
        <div aria-labelledby="touch-layout-dialog-title" aria-modal="true" className="bwf-touch-layout-overlay" role="dialog">
          <div className="bwf-touch-layout-dialog">
            <header className="bwf-touch-layout-head">
              <div>
                <p className="bwf-touch-layout-kicker">TOUCH CONTROL / LAYOUT</p>
                <h1 id="touch-layout-dialog-title">画面キーパッドの配置</h1>
                <p className="bwf-touch-layout-lead">実際のゲーム画面と同じ縦横比で、ボタンをドラッグして配置します。</p>
              </div>
              <button aria-label="配置画面を閉じる" className="bwf-touch-layout-close" onClick={closeEditor} type="button">×</button>
            </header>

            <div className="bwf-touch-layout-toolbar">
              <div aria-label="配置する画面方向" className="bwf-touch-layout-tabs" role="tablist">
                {(["portrait", "landscape"] as const).map((orientation) => (
                  <button
                    aria-selected={editorOrientation === orientation}
                    className={`bwf-touch-layout-tab${editorOrientation === orientation ? " is-selected" : ""}`}
                    key={orientation}
                    onClick={() => setEditorOrientation(orientation)}
                    role="tab"
                    type="button"
                  >
                    {orientationLabel(orientation)}
                  </button>
                ))}
              </div>
              <span className="bwf-touch-layout-orientation-note">
                {orientationLabel(editorOrientation)}の配置を編集中。決定すると保存されます。
              </span>
            </div>

            <div className="bwf-touch-layout-stage-wrap">
              <div
                aria-label={`${orientationLabel(editorOrientation)}のゲーム画面配置`}
                className={`bwf-touch-layout-stage${editorOrientation === "portrait" ? " is-portrait" : ""}`}
              >
                <span aria-hidden="true" className="bwf-touch-layout-board" />
                <span aria-hidden="true" className="bwf-touch-layout-next" />
                <span aria-hidden="true" className="bwf-touch-layout-status">STATUS / VS</span>
                <span aria-hidden="true" className="bwf-touch-layout-label">BLOCK BATTLE</span>
                {CONTROLS.map(({ name, glyph, label }) => {
                  const position = draftLayout[name];
                  return (
                    <button
                      aria-label={`${label}ボタンを移動`}
                      className={`bwf-touch-layout-control${dragging === name ? " is-dragging" : ""}`}
                      data-control={name}
                      key={name}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.setPointerCapture(event.pointerId);
                        setDragging(name);
                        moveDraftControl(name, event.clientX, event.clientY, event.currentTarget.parentElement as HTMLDivElement);
                      }}
                      onPointerMove={(event) => {
                        if (dragging === name) {
                          moveDraftControl(name, event.clientX, event.clientY, event.currentTarget.parentElement as HTMLDivElement);
                        }
                      }}
                      onPointerUp={() => setDragging(null)}
                      onPointerCancel={() => setDragging(null)}
                      style={{ left: `${position.x}%`, top: `${position.y}%` }}
                      type="button"
                    >
                      <span aria-hidden="true">{glyph}</span>
                      <small>{label}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bwf-touch-layout-actions">
              <button
                className="secondary-button bwf-touch-layout-reset-action"
                onClick={() => setDraftSettings((current) => updateTouchControlsLayout(current, editorOrientation, DEFAULT_TOUCH_CONTROLS_LAYOUT))}
                type="button"
              >
                この向きを初期化
              </button>
              <button className="secondary-button" onClick={closeEditor} type="button">キャンセル</button>
              <button className="chrome-button" onClick={() => { onChange(draftSettings); closeEditor(); }} type="button">決定</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
